<template>
  <div
    class="bg-background-neutral-secondary border-situational-lightgrey space-y-3 rounded-3xl border px-4 py-3"
  >
    <h4
      v-if="!hasSameDestinationCity"
      class="text-text-neutral-primary text-sm"
    >
      {{ directionText }}
    </h4>
    <section class="min-w-64 space-y-2">
      <OfferPrice
        :offer="offer as unknown as flightEntity.types.MappedOffer"
        :offer-idx="offerIdx"
        :offer-price="offer.price.amount"
        :source="source"
        :cashback-click-source="cashbackClickSource"
        @onCashbackInfoClick="emit('onCashbackClick')"
      />
      <div class="flex items-start space-x-3">
        <div class="flex items-center space-x-2">
          <AirlineIconsGroup
            :airlines="uniqueAirlines"
            :airlines-to-display="2"
            is-vertical
          />
          <div class="ml-2 space-y-1">
            <p class="text-text-neutral-primary text-sm">
              {{ dayjs(offer.dep_at).format('D MMMM') }}
            </p>
            <p class="text-text-neutral-quaternary whitespace-nowrap text-sm">
              {{ flightTimeText }}
            </p>
          </div>
        </div>
        <div class="ml-4 space-y-1">
          <p class="text-text-neutral-primary whitespace-nowrap text-sm">
            {{ flightDurationText }}
          </p>
          <p class="text-text-neutral-quaternary text-sm">
            {{ transfersText }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { uniqBy } from 'lodash-es';
  import dayjs from 'dayjs';
  import { dateUtils } from 'shared';
  import { AirlineIconsGroup, type flightEntity } from 'freedom-shared';

  import { OfferPrice } from '@/widgets/search-result-card';

  import type { hotOffersTypes } from '@/entities/hot-offers';

  import type { analyticsEventsLib } from '@/shared/lib';

  const props = defineProps<{
    offer: hotOffersTypes.MappedHotOffer;
    offerIdx: number;
    hasSameDestinationCity?: boolean;
    source?: string;
    cashbackClickSource: analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource;
  }>();

  const emit = defineEmits<{ onCashbackClick: [] }>();

  const { t } = useI18n();

  const directionText = computed(() => {
    return `${props.offer.origin.city.name} — ${props.offer.destination.city.name}`;
  });

  const uniqueAirlines = computed(() => {
    return uniqBy(props.offer.display_airlines, 'code').map(airline => ({
      title: airline.name ?? '',
      iata: airline.code,
    }));
  });

  const flightTimeText = computed(() => {
    return `${dayjs(props.offer.dep_at).format('HH:mm')} - ${dayjs(
      props.offer.arr_at,
    ).format('HH:mm')}`;
  });

  const flightDurationText = computed(() => {
    const duration = dateUtils.prettyDurationFromSeconds(
      props.offer.flight_time,
    );

    return t('hotOffers.v2.offer.duration', { time: duration });
  });

  const transfersText = computed(() => {
    return props.offer.is_direct
      ? t('hotOffers.v2.offer.direct')
      : t('hotOffers.v2.offer.transfers', {
          n: props.offer.segments_quantity - 1,
        });
  });
</script>
